<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
    useReferenceSelectionStore,
    type ReferenceTag,
} from '../../stores/referenceSelectionStore';

/**
 * Session-only tag display for one reference.
 *
 * Up to `maxPills` tags render as colored pills (default 2 — the table
 * rows' compact mode). Beyond that, an icon + total-count indicator
 * appears whose hover tooltip lists all the tags — replacing the pills by
 * default, or after them when `overflowAppend` is set (the raw-view
 * header mode). The
 * tooltip is teleported to <body> and fixed-positioned to the right of the
 * indicator so scroll containers can't clip it. Renders nothing when the
 * reference has no tags.
 */

const props = withDefaults(
    defineProps<{
        contentHash?: string;
        maxPills?: number;
        overflowAppend?: boolean;
    }>(),
    { contentHash: undefined, maxPills: 2, overflowAppend: false },
);

const selectionStore = useReferenceSelectionStore();

const tags = computed<ReferenceTag[]>(() => selectionStore.tagsForRow(props.contentHash));

const overflowing = computed(() => tags.value.length > props.maxPills);

const visiblePills = computed<ReferenceTag[]>(() => {
    if (!overflowing.value) return tags.value;
    return props.overflowAppend ? tags.value.slice(0, props.maxPills) : [];
});

const pillStyle = (tag: ReferenceTag) => {
    const color = selectionStore.colorForTag(tag);
    return { background: color.bg, color: color.text };
};

const indicatorEl = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);
const tooltipStyle = ref<Record<string, string>>({});

const TOOLTIP_GAP = 6;
const TOOLTIP_MAX_WIDTH = 240;

const showTooltip = () => {
    const el = indicatorEl.value;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Open to the right with the tooltip's top-left corner on the
    // indicator's top-right corner; clamp so a narrow window doesn't push
    // the tooltip off-screen.
    const left = Math.min(
        rect.right + TOOLTIP_GAP,
        window.innerWidth - TOOLTIP_MAX_WIDTH - TOOLTIP_GAP,
    );
    tooltipStyle.value = {
        left: `${left}px`,
        top: `${rect.top}px`,
    };
    tooltipVisible.value = true;
};

const hideTooltip = () => {
    tooltipVisible.value = false;
};

// The tooltip is position: fixed, so it would float in place while the
// table scrolls under it — hide it on any scroll instead (capture catches
// the inner table container's scroll, which doesn't bubble).
watch(tooltipVisible, (visible) => {
    if (visible) {
        window.addEventListener('scroll', hideTooltip, { capture: true });
    } else {
        window.removeEventListener('scroll', hideTooltip, { capture: true });
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', hideTooltip, { capture: true });
});
</script>

<template>
    <div v-if="tags.length > 0" class="crv-row-tags">
        <span
            v-for="tag in visiblePills"
            :key="tag.name"
            class="reference-tag-pill"
            :style="pillStyle(tag)"
            >{{ tag.name }}</span
        >
        <span
            v-if="overflowing"
            ref="indicatorEl"
            class="crv-tag-indicator"
            @click.stop
            @mouseenter="showTooltip"
            @mouseleave="hideTooltip"
        >
            <span class="material-icons-outlined">sell</span>{{ tags.length }}
        </span>
        <Teleport to="body">
            <span v-if="tooltipVisible" class="crv-tag-tooltip" :style="tooltipStyle">
                <span
                    v-for="tag in tags"
                    :key="tag.name"
                    class="reference-tag-pill"
                    :style="pillStyle(tag)"
                    >{{ tag.name }}</span
                >
            </span>
        </Teleport>
    </div>
</template>
